import { PencilIcon } from '@heroicons/react/24/outline';
import { useCallback } from 'react';
import { Link, useParams } from 'react-router';
import { type GetCommentParams, getPartnerProposalComments, postComment } from '@/api/comments';
import type { ChangeOrderCommentsResourceType, ChangeOrderRefType } from '@/api/Sherpa/ChangeOrder';
import Comments from '@/components/Comments';
import ImageCircularAvatar from '@/components/UI/Avatar/ImageCircularAvatar';
import Badge from '@/components/UI/Badge';
import HistoryTimeline from '@/components/UI/HistoryTimeline';
import { SherpaProjectReactQueryKey } from '@/constants/ReactQueryKeys';
import type { ChangeOrderType } from '@/features/ChangeOrder/types';
import { convertEnumToText, formatDate3, getInitials } from '@/utils';
import Approvals from './Approvals';

type ChangeOrderDetailsProps = {
  type: ChangeOrderType;
  resourceType: ChangeOrderRefType;
  commentResourceType: ChangeOrderCommentsResourceType;
  title?: string;
  additionalInfo?: { title: string; value: string }[];
  details?: { title: string; value: string }[];
  status: string;
  submittedBy?: {
    id?: number;
    company: string;
    email: string;
    createdAt: string;
    name: string;
    profileImageUrl?: string;
    title?: string;
  };
  onEdit?: () => void;
};

export function ChangeOrderDetails({
  type,
  resourceType,
  commentResourceType,
  title,
  details,
  additionalInfo,
  status,
  submittedBy,
  onEdit,
}: ChangeOrderDetailsProps) {
  const { changeOrderId = '' } = useParams();

  const generalInfo = [
    {
      title: 'Submitter Company',
      value: submittedBy?.company,
    },
    {
      title: 'Submitter E-mail',
      value: submittedBy?.email,
    },
    {
      title: 'Submitted Date',
      value: submittedBy?.createdAt ? formatDate3(submittedBy?.createdAt) : '',
    },
  ];

  const postComments = useCallback(
    async (values: { comment: string }) => {
      const response = await postComment({
        data: {
          resource_id: changeOrderId!,
          resource_type: commentResourceType,
          ...values,
        },
      });
      return response;
    },
    [changeOrderId, commentResourceType]
  );

  const getComments = useCallback(
    (_params: GetCommentParams) => {
      return getPartnerProposalComments({
        params: {
          resource_id: changeOrderId!,
          resource_type: commentResourceType,
        },
      });
    },
    [changeOrderId, commentResourceType]
  );

  const getQueryKey = () => {
    switch (type) {
      case 'budget':
        return SherpaProjectReactQueryKey.GET_CHANGE_ORDER_BUDGET;
      case 'deliverable':
        return SherpaProjectReactQueryKey.GET_CHANGE_ORDER_DELIVERABLES;
      case 'milestone':
        return SherpaProjectReactQueryKey.GET_CHANGE_ORDER_MILESTONES;
    }
  };

  return (
    <div className='min-h-full max-w-screen-2xl mx-auto'>
      <main>
        <div className='sm:px-6 mt-8 w-full mx-auto grid grid-cols-12 gap-6'>
          <div className='space-y-6 col-span-12 xl:col-span-8'>
            <section>
              <div className='bg-white shadow sm:rounded-lg px-6 py-5'>
                <div className='pb-5 pt-3 flex justify-between items-center border-b-2'>
                  <h2 className='capitalize m-0'>{title} C/O Detail</h2>
                  <div className='flex justify-between items-center gap-3'>
                    <Badge status={status} text={convertEnumToText(status)} />
                    {status !== 'APPROVED' && <PencilIcon className='h-5 w-5 text-neutralColor-normal cursor-pointer' onClick={onEdit} />}
                  </div>
                </div>
                <div className='py-4'>
                  {submittedBy && (
                    <div className='flex flex-col justify-between'>
                      <h4 className='font-semibold'>Submitted By</h4>
                      <div className='flex pt-2'>
                        <Link to={`/app/administration/users/${submittedBy?.id}`} className='flex items-center gap-2'>
                          <ImageCircularAvatar
                            customClass='h-16 w-16'
                            imageUrl={submittedBy?.profileImageUrl as string}
                            fallbackText={getInitials(submittedBy?.name)}
                          />
                          <div>
                            <p className='text-base font-semibold'>{submittedBy?.name}</p>
                            <p className='text-neutralColor-normal text-sm'>{submittedBy?.title}</p>
                          </div>
                        </Link>
                      </div>
                    </div>
                  )}
                  <div className='flex flex-col gap-4 py-6'>
                    {generalInfo.map((info) => (
                      <dl key={info.title} className='flex gap-2'>
                        <dt className='text-neutralColor-normal'>{info.title}:</dt>
                        <dd className=''>{info.value}</dd>
                      </dl>
                    ))}
                  </div>
                  <hr className='border-gray-300' />
                  <div className='py-6'>
                    <h3 className='m-0 mb-4 capitalize'>{type} details</h3>
                    <div className='grid grid-cols-2 gap-4'>
                      {details?.map((info) => (
                        <dl key={info.title} className='flex gap-2'>
                          <dt className='text-neutralColor-normal'>
                            {info.title}
                            {info.title ? ':' : ''}
                          </dt>
                          <dd className=''>{info.value}</dd>
                        </dl>
                      ))}
                    </div>
                  </div>
                  <hr className='border-gray-300' />
                  <div className='flex flex-col gap-5 pt-6'>
                    {additionalInfo?.map((info) => (
                      <dl key={info.title}>
                        <dt className='font-semibold mb-2'>{info.title}</dt>
                        <dd className='text-neutralColor-normal'>{info.value}</dd>
                      </dl>
                    ))}
                  </div>
                </div>
              </div>
            </section>
            <Comments postComments={postComments} getComments={getComments} queryKey='invoice-comments' />
          </div>
          <div className='space-y-6 col-span-12 xl:col-span-4'>
            <Approvals title={type} refId={Number(changeOrderId)} refType={resourceType} type={type} queryKey={getQueryKey()} />
            <HistoryTimeline
              activity={{
                id: changeOrderId,
                createdAt: submittedBy?.createdAt,
              }}
              resourceType={resourceType}
            />
          </div>
        </div>
      </main>
    </div>
  );
}

export default ChangeOrderDetails;
